import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CiUser } from 'react-icons/ci';
import { FaRegFileAlt } from 'react-icons/fa';
import { FiFileText, FiClock, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { MdOutlineWorkOutline } from 'react-icons/md';
import { AiOutlineClockCircle } from 'react-icons/ai';
import { LiaNotesMedicalSolid } from 'react-icons/lia';
import { TbEdit } from 'react-icons/tb';
import PopupWrapper from '../utils/PopupWrapper';
import InputField from '../ui/InputField';
import TaskAssignmentTable from '../Projects/TaskAssignmentTable';
import { TaskItem } from '../../redux/types/taskSheetTypes';

const STATUS_COLORS: Record<string, string> = {
  ASSIGNED:         'bg-sky-100 text-sky-700',
  IN_PROGRESS:      'bg-violet-100 text-violet-700',
  REVIEW:           'bg-orange-100 text-orange-700',
  DELIVERED:        'bg-green-100 text-green-700',
  BILLED:           'bg-emerald-100 text-emerald-700',
  PAYMENT_RECEIVED: 'bg-teal-100 text-teal-700',
  HOLD:             'bg-yellow-100 text-yellow-700',
  CANCELLED:        'bg-red-100 text-red-700',
  REOPEN:           'bg-purple-100 text-purple-700',
};

interface Props {
  task: TaskItem | null;
  isOpen: boolean;
  onClose: () => void;
}

const TaskRowPopup: React.FC<Props> = ({ task, isOpen, onClose }) => {
  const navigate = useNavigate();
  const [showHistory, setShowHistory] = useState(false);

  if (!task) return null;

  const latestAssignment = task.assignmentHistory?.[0];
  const updatedDate = latestAssignment?.assignedDate
    ? new Date(latestAssignment.assignedDate).toLocaleDateString('en-IN')
    : new Date().toLocaleDateString('en-IN');
  const updatedTime = latestAssignment?.assignedDate
    ? new Date(latestAssignment.assignedDate).toLocaleTimeString('en-IN')
    : new Date().toLocaleTimeString('en-IN');

  const statusColor = STATUS_COLORS[task.status] || 'bg-gray-100 text-gray-800';

  // Map assignmentHistory to the format TaskAssignmentTable expects
  const assignments = (task.assignmentHistory || []).map(h => ({
    id: h.id,
    assignedBy: h.assignedBy,
    assignedTo: h.assignedTo,
    status: h.status,
    assignedDate: h.assignedDate,
  }));

  return (
    <PopupWrapper
      title={task.projectName}
      isOpen={isOpen}
      onClose={onClose}
      size="mlg"
      logo={
        <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
          <FiFileText className="text-gray-400 text-lg" />
        </div>
      }
      footer={
        <button
          onClick={() => { onClose(); navigate(`/edit-project/${task.projectId}`); }}
          className="w-full px-4 py-4 text-lg font-semibold rounded-lg flex items-center border-2 justify-center gap-2 bg-lime-100 hover:bg-lime-200 border-lime-600 text-lime-700 transition-colors"
        >
          <TbEdit className="mr-1" /> Edit Project
        </button>
      }
      headerComponent={
        <div className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium ${statusColor}`}>
          {task.displayStatus || task.status}
        </div>
      }
    >
      <div className="flex flex-col pb-4">
        <div className="text-sm text-gray-500">
          Last updated on — {updatedDate} ({updatedTime})
        </div>
      </div>

      <div className="flex flex-col pt-2">
        <div className="grid grid-cols-2 gap-6">
          <InputField
            label="Company Name"
            value={task.customerName}
            className="bg-gray-100"
            disabled
            icon={<CiUser className="w-5 h-5" />}
          />
          <InputField
            label="# Manak ID"
            value={task.mId}
            className="bg-gray-100"
            disabled
            icon={<FaRegFileAlt className="w-5 h-5" />}
          />
          <InputField
            label="Project Name"
            value={task.projectName}
            className="bg-gray-100"
            disabled
            icon={<FiFileText className="w-5 h-5" />}
          />
          <InputField
            label="Assigned By"
            value={task.assignedBy}
            className="bg-gray-100"
            disabled
            icon={<MdOutlineWorkOutline className="w-5 h-5" />}
          />
          <InputField
            label="Amount"
            value={task.projectAmount ? `₹${task.projectAmount.toLocaleString('en-IN')}` : '—'}
            className="bg-gray-100"
            disabled
            icon={<AiOutlineClockCircle className="w-5 h-5" />}
          />

          <button
            onClick={() => setShowHistory(prev => !prev)}
            className={`w-full px-4 py-3 rounded-lg transition-colors border-2 flex items-center justify-between
              ${showHistory
                ? 'bg-blue-50 border-blue-600 text-blue-700'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
          >
            <div className="flex items-center gap-2">
              <FiClock className="h-5 w-5" />
              <span className="font-medium">View History</span>
            </div>
            {showHistory ? <FiChevronUp className="h-5 w-5" /> : <FiChevronDown className="h-5 w-5" />}
          </button>
        </div>

        <div className="mt-6">
          <InputField
            label="Note/Remarks"
            value={task.notes || '—'}
            type="textarea"
            className="bg-gray-100"
            disabled
            icon={<LiaNotesMedicalSolid className="w-5 h-5" />}
          />
        </div>

        <div
          className={`mt-3 transition-all duration-300 ${showHistory ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}
        >
          <TaskAssignmentTable assignments={assignments} visible={showHistory} />
        </div>
      </div>
    </PopupWrapper>
  );
};

export default TaskRowPopup;
